import { z } from 'zod';
import { baseString } from './core/primitives';
import { str, emptyToUndefined } from './core/helpers';
import { strongPassword } from './auth';

const USERNAME_MIN = 3;
const USERNAME_MAX = 50;
const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 15;

// ── Field builders ─────────────────────────────────────────────────────────

const username = (t) =>
  z.preprocess(
    str,
    baseString
      .min(1, t('validation.user.username.required'))
      .min(
        USERNAME_MIN,
        t('validation.user.username.minLength', { min: USERNAME_MIN })
      )
      .max(
        USERNAME_MAX,
        t('validation.user.username.maxLength', { max: USERNAME_MAX })
      )
  );

const email = (t) =>
  z.preprocess(
    str,
    baseString
      .toLowerCase()
      .min(1, t('validation.user.email.required'))
      .pipe(z.email(t('validation.user.email.invalid')))
  );

const roleId = (t) =>
  z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return -1;
      if (typeof val === 'string') {
        const parsed = parseInt(val, 10);
        return isNaN(parsed) ? val : parsed;
      }
      return val;
    },
    z.number().int().positive(t('validation.user.role.required'))
  );

const phone = (t) =>
  z.preprocess(
    (val) => {
      if (val === '' || val === null || val === undefined) return undefined;
      return String(val).trim();
    },
    baseString
      .refine(
        (val) => {
          const digits = val.replace(/\D/g, '');
          return digits.length >= PHONE_MIN_DIGITS;
        },
        t('validation.user.phone.minDigits', { min: PHONE_MIN_DIGITS })
      )
      .refine(
        (val) => {
          const digits = val.replace(/\D/g, '');
          return digits.length <= PHONE_MAX_DIGITS;
        },
        t('validation.user.phone.maxDigits', { max: PHONE_MAX_DIGITS })
      )
      .optional()
  );

// ── Schemas ────────────────────────────────────────────────────────────────

export const createUserSchema = (t) =>
  z.object({
    username: username(t),
    email: email(t),
    password: strongPassword(t),
    role_id: roleId(t),
    phone: phone(t),
  });

export const updateUserSchema = (t) =>
  z.object({
    username: username(t).optional(),
    email: emptyToUndefined(email(t).optional()),
    password: emptyToUndefined(strongPassword(t).optional()),
    role_id: roleId(t).optional(),
    phone: phone(t),
    active: z
      .preprocess((val) => {
        if (val === undefined || val === null) return undefined;
        if (val === true || val === 1 || val === '1') return true;
        if (val === false || val === 0 || val === '0') return false;
        return val;
      }, z.boolean().optional())
      .optional(),
  });

export const updateProfileSchema = (t) =>
  z.object({
    username: emptyToUndefined(username(t).optional()),
    phone: phone(t),
  });

/** Verify current password before allowing password change */
export const verifyMyPasswordSchema = (t) =>
  z.object({
    currentPassword: z.preprocess(
      str,
      baseString.min(1, t('validation.currentPassword.required'))
    ),
  });

/** Change password (with optional current-password gate) */
export const changeMyPasswordSchema = (t, requireCurrentPassword = true) => {
  const shape = {
    newPassword: strongPassword(t, 'newPassword'),
    confirmPassword: z.preprocess(
      str,
      baseString.min(1, t('validation.confirmPassword.required'))
    ),
  };

  if (requireCurrentPassword) {
    shape.currentPassword = z.preprocess(
      str,
      baseString.min(1, t('validation.currentPassword.required'))
    );
  }

  return z
    .object(shape)
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('validation.confirmPassword.mismatch'),
      path: ['confirmPassword'],
    });
};

export { USERNAME_MIN, USERNAME_MAX, PHONE_MIN_DIGITS, PHONE_MAX_DIGITS };
